import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Mail, Phone, Linkedin, MapPin, Building2, Users, Tag as TagIcon, Copy, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useT } from "@/i18n/LanguageContext";

const initials = (name = "") => name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

const copyText = (text, msg) => {
  navigator.clipboard.writeText(text || "");
  toast.success(msg);
};

const KV = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  </div>
);

export const LeadDetailDrawer = ({ lead, open, onOpenChange, statusOptions, lists, onChanged, onAddToList }) => {
  const tr = useT();
  const [tagInput, setTagInput] = React.useState("");
  const [local, setLocal] = React.useState(lead);

  React.useEffect(() => setLocal(lead), [lead]);
  if (!local) return null;

  const patch = async (data) => {
    try {
      const updated = await api.updateLead(local.id, data);
      setLocal(updated);
      onChanged && onChanged(updated);
    } catch (e) {
      toast.error(tr("update_failed"));
    }
  };

  const addTag = async () => {
    const val = tagInput.trim();
    if (!val) return;
    const tags = Array.from(new Set([...(local.tags || []), val]));
    setTagInput("");
    await patch({ tags });
  };
  const removeTag = async (val) => patch({ tags: (local.tags || []).filter((x) => x !== val) });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="lead-detail-drawer"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[480px]"
      >
        <SheetHeader className="border-b p-4 text-left">
          <div className="flex items-center gap-3 pr-8">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials(local.full_name)}</AvatarFallback>
            </Avatar>
            <div>
              <SheetTitle className="text-lg">{local.full_name}</SheetTitle>
              <div className="text-sm text-muted-foreground">{local.job_title}</div>
              <div className="text-sm font-medium text-primary">{local.company_name}</div>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {/* Contact */}
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{tr("contact")}</div>
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2">
              <div className="flex items-center gap-2 overflow-hidden">
                <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate font-mono text-xs">{local.email}</span>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="lead-detail-copy-email-button" onClick={() => copyText(local.email, tr("email_copied"))} aria-label="Copy email">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2">
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span className="font-mono text-xs">{local.phone || "—"}</span></div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyText(local.phone, tr("phone_copied"))} aria-label="Copy phone"><Copy className="h-3.5 w-3.5" /></Button>
            </div>
            <a href={local.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs text-primary hover:bg-secondary/60">
              <Linkedin className="h-4 w-4" /> <span className="truncate">{local.linkedin_url}</span>
            </a>
          </div>

          <Separator />

          {/* Meta */}
          <div className="grid grid-cols-2 gap-4">
            <KV icon={MapPin} label={tr("location")} value={local.location} />
            <KV icon={Building2} label={tr("sub_industry")} value={local.sub_industry} />
            <KV icon={Users} label={tr("company_size")} value={local.company_size} />
            <KV icon={TagIcon} label={tr("seniority")} value={local.seniority} />
          </div>

          <Separator />

          {/* Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{tr("outreach_status")}</div>
              <StatusBadge status={local.status} />
            </div>
            <Select value={local.status} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger data-testid="lead-detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(statusOptions || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{tr("tags")}</div>
            <div className="flex flex-wrap gap-1.5">
              {(local.tags || []).map((tg) => (
                <Badge key={tg} variant="secondary" className="gap-1">
                  {tg}
                  <button onClick={() => removeTag(tg)} aria-label={`Remove ${tg}`}><X className="h-3 w-3" /></button>
                </Badge>
              ))}
              {(local.tags || []).length === 0 && <span className="text-xs text-muted-foreground">{tr("no_tags")}</span>}
            </div>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder={tr("add_tag")} data-testid="lead-detail-tag-input" onKeyDown={(e) => e.key === "Enter" && addTag()} />
              <Button variant="outline" size="icon" onClick={addTag} data-testid="lead-detail-add-tag-button" aria-label="Add tag"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          {/* Add to list */}
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{tr("add_to_list")}</div>
            <Select onValueChange={(v) => onAddToList && onAddToList(v, [local.id])}>
              <SelectTrigger data-testid="lead-detail-add-to-list-button"><SelectValue placeholder={lists && lists.length ? tr("select_list") : tr("no_lists_yet")} /></SelectTrigger>
              <SelectContent>
                {(lists || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
